/**
 * Estructura global: cabecera con navegación principal, menú de cuenta,
 * cambio de tema/idioma, área principal, pie y contenedor de notificaciones.
 */
import { hasRole } from './permissions';
import { h, replace } from '../lib/dom';
import { onLocaleChange, t, type MessageKey } from '../lib/i18n';
import { icon, logo } from '../components/icons';
import { avatar, toast } from '../components/ui';
import { navigate, onRouteChange, reload, type RouteDef } from './router';
import { getState, logout, subscribe, updatePrefs } from './store';

const NAV: { path: string; label: MessageKey; icon: string }[] = [
  { path: '/', label: 'nav.home', icon: 'home' },
  { path: '/learn', label: 'nav.learn', icon: 'book' },
  { path: '/practice', label: 'nav.practice', icon: 'target' },
  { path: '/test', label: 'nav.test', icon: 'bolt' },
  { path: '/games', label: 'nav.games', icon: 'gamepad' },
  { path: '/compete', label: 'nav.compete', icon: 'trophy' },
  { path: '/stats', label: 'nav.stats', icon: 'chart' },
  { path: '/profile', label: 'nav.profile', icon: 'user' },
];

function isActive(path: string, current: string): boolean {
  return path === '/' ? current === '/' : current === path || current.startsWith(`${path}/`);
}

export function createLayout(): { root: HTMLElement; main: HTMLElement } {
  const main = h('main', { id: 'main', class: 'main', tabindex: '-1' });
  const navList = h('ul', { class: 'nav-list' });
  const mobileNav = h('nav', { class: 'mobile-nav', attrs: { 'aria-label': t('nav.mainNav') } });
  const actions = h('div', { class: 'header-actions' });
  const menuButton = h('button', { class: 'icon-btn menu-btn', attrs: { 'aria-label': t('nav.menu'), 'aria-expanded': 'false', 'aria-controls': 'mobile-nav' } }, icon('menu'));
  mobileNav.id = 'mobile-nav';
  const announcement = h('div', { class: 'announcement', hidden: true, role: 'status' });
  const header = h(
    'header',
    { class: 'site-header' },
    h(
      'div',
      { class: 'header-inner' },
      h('a', { href: '/', class: 'brand', attrs: { 'aria-label': 'ManiType' } }, logo(30), h('span', { class: 'brand-name' }, 'Mani', h('b', null, 'Type'))),
      h('nav', { class: 'main-nav', attrs: { 'aria-label': t('nav.mainNav') } }, navList),
      actions,
      menuButton,
    ),
    mobileNav,
  );
  const footer = h('footer', { class: 'site-footer' });
  const root = h('div', { class: 'app-shell' }, announcement, header, main, footer, h('div', { id: 'toasts', class: 'toasts', attrs: { 'aria-live': 'polite' } }));

  let currentPath = location.pathname;
  let accountOpen = false;

  const closeMobile = () => {
    mobileNav.classList.remove('open');
    menuButton.setAttribute('aria-expanded', 'false');
  };
  menuButton.addEventListener('click', () => {
    const open = !mobileNav.classList.contains('open');
    mobileNav.classList.toggle('open', open);
    menuButton.setAttribute('aria-expanded', String(open));
  });

  const renderNav = () => {
    const items = NAV.map((item) =>
      h('li', null, h('a', { href: item.path, class: 'nav-link', title: t(item.label), attrs: { 'aria-label': t(item.label), 'aria-current': isActive(item.path, currentPath) ? 'page' : null } }, icon(item.icon, 18), h('span', null, t(item.label)))),
    );
    replace(navList, items);
    replace(
      mobileNav,
      h('ul', null, NAV.map((item) =>
        h('li', null, h('a', { href: item.path, class: 'nav-link', onClick: closeMobile, attrs: { 'aria-current': isActive(item.path, currentPath) ? 'page' : null } }, icon(item.icon, 20), t(item.label))),
      )),
    );
  };

  const renderActions = () => {
    const { user, prefs } = getState();
    const dark = !['paper', 'sand', 'sakura'].includes(prefs.theme);
    const themeButton = h(
      'button',
      {
        class: 'icon-btn',
        title: t('nav.themeToggle'),
        attrs: { 'aria-label': t('nav.themeToggle') },
        onClick: () => void updatePrefs({ theme: dark ? 'paper' : 'midnight' }).catch(() => toast(t('common.error'), 'error')),
      },
      icon(dark ? 'sun' : 'moon'),
    );
    const langButton = h(
      'button',
      {
        class: 'icon-btn lang-btn',
        title: t('nav.language'),
        attrs: { 'aria-label': `${prefs.uiLanguage.toUpperCase()} · ${t('nav.language')}` },
        onClick: () => void updatePrefs({ uiLanguage: prefs.uiLanguage === 'es' ? 'en' : 'es' }),
      },
      prefs.uiLanguage.toUpperCase(),
    );
    if (!user) {
      replace(actions, themeButton, langButton, h('a', { href: '/login', class: 'btn btn-ghost btn-sm' }, t('nav.login')), h('a', { href: '/register', class: 'btn btn-sm hide-sm' }, t('nav.register')));
      return;
    }
    const menu = h(
      'div',
      { class: 'account-menu', hidden: !accountOpen, role: 'menu' },
      h('div', { class: 'account-head' }, h('strong', null, user.profile.displayName), h('span', null, `@${user.username} · ${t('common.level')} ${user.profile.level.level}`)),
      h('a', { href: `/u/${user.username}`, role: 'menuitem' }, icon('user', 18), t('nav.profile')),
      h('a', { href: '/stats', role: 'menuitem' }, icon('chart', 18), t('nav.stats')),
      h('a', { href: '/certificates', role: 'menuitem' }, icon('award', 18), t('nav.certificates')),
      h('a', { href: '/settings', role: 'menuitem' }, icon('settings', 18), t('nav.settings')),
      hasRole(user.role, 'editor') ? h('a', { href: '/admin', role: 'menuitem' }, icon('shield', 18), t('nav.admin')) : null,
      h(
        'button',
        {
          role: 'menuitem',
          onClick: async () => {
            await logout();
            toast(t('auth.loggedOut'), 'success');
            navigate('/');
          },
        },
        icon('logout', 18),
        t('nav.logout'),
      ),
    );
    const trigger = h(
      'button',
      {
        class: 'account-trigger',
        attrs: { 'aria-haspopup': 'menu', 'aria-expanded': String(accountOpen), 'aria-label': t('nav.account') },
        onClick: (e: MouseEvent) => {
          e.stopPropagation();
          accountOpen = !accountOpen;
          renderActions();
        },
      },
      avatar(user.profile.avatar, user.profile.avatarColor, 32),
      user.profile.currentStreak > 0 ? h('span', { class: 'streak-chip', title: t('profile.streak') }, icon('flame', 14), String(user.profile.currentStreak)) : null,
    );
    replace(actions, themeButton, langButton, h('div', { class: 'account' }, trigger, menu));
  };

  const renderFooter = () => {
    replace(
      footer,
      h(
        'div',
        { class: 'footer-inner' },
        h('div', { class: 'footer-brand' }, h('a', { href: '/', class: 'brand' }, logo(24), h('span', { class: 'brand-name' }, 'Mani', h('b', null, 'Type'))), h('p', null, t('footer.about'))),
        h(
          'nav',
          { class: 'footer-links', attrs: { 'aria-label': 'footer' } },
          h('a', { href: '/learn' }, t('nav.learn')),
          h('a', { href: '/test' }, t('nav.test')),
          h('a', { href: '/compete' }, t('nav.compete')),
          h('a', { href: '/about/metrics' }, t('footer.formulas')),
          h('a', { href: '/verify' }, t('footer.verify')),
        ),
        h('p', { class: 'footer-note' }, `© ${new Date().getFullYear()} ManiType · ${t('footer.rights')}`),
      ),
    );
  };

  const renderAnnouncement = () => {
    const text = getState().publicSettings?.announcement ?? '';
    announcement.hidden = !text;
    replace(announcement, text);
  };

  const renderAll = () => {
    renderNav();
    renderActions();
    renderFooter();
    renderAnnouncement();
  };

  document.addEventListener('click', () => {
    if (accountOpen) {
      accountOpen = false;
      renderActions();
    }
  });
  // Escape cierra el menú abierto y devuelve el foco al botón que lo abrió.
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    if (accountOpen) {
      accountOpen = false;
      renderActions();
      header.querySelector<HTMLElement>('.account-trigger')?.focus();
    }
    if (mobileNav.classList.contains('open')) {
      closeMobile();
      menuButton.focus();
    }
  });

  onRouteChange((path, route: RouteDef | null) => {
    currentPath = path;
    accountOpen = false;
    root.classList.toggle('focus-mode', Boolean(route?.focus));
    closeMobile();
    renderNav();
    renderActions();
  });
  subscribe((state, previous) => {
    if (state.user !== previous.user || state.prefs !== previous.prefs) renderActions();
    if (state.publicSettings !== previous.publicSettings) renderAnnouncement();
    // Al iniciar o cerrar sesión se recarga la página actual (permisos y datos cambian).
    if (Boolean(state.user) !== Boolean(previous.user)) reload();
  });
  // El enlace para saltar al contenido está en index.html (debe existir antes del JS): se traduce aquí.
  const translateSkipLink = () => {
    const skip = document.querySelector<HTMLAnchorElement>('.skip-link');
    if (skip) skip.textContent = t('nav.skipToContent');
  };
  onLocaleChange(() => {
    translateSkipLink();
    renderAll();
    reload();
  });
  translateSkipLink();
  renderAll();
  return { root, main };
}
